import { Box, Stack, Typography } from '@mui/material'
import { ThemeProvider, alpha, useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import BeastWordmark from 'src/components/BeastWordmark'
import Illus, { IllusProps } from 'src/components/Illus'
// Deep import, not the `src/hooks` barrel. This file is on the sign-in path,
// which is the first screen anybody sees, and the barrel re-exports
// `useFirebaseAssets`, so importing one media-query hook through it drags
// axios and lodash onto the boot graph. Measured: the routes chunk went from
// 50 kB to 96 kB gzip the moment this line used the barrel.
import useResponsive from 'src/hooks/useResponsive'
import { createAppTheme } from 'src/theme/createAppTheme'
import { crossfade } from 'src/theme/motion'
import { colorTokens, layout } from 'src/theme/tokens.generated'

// ----------------------------------------------------------------------
// The shell every screen outside the console shares: sign in, and whatever
// else has to stand on its own before there is an account to work in.
//
// A brand panel in ink on the left and the task on the right. The panel is
// the only place in the product where art leads, so it is the only place the
// illustration tier appears at its larger steps, and the only place the fleet
// appears as photography rather than as a fact in a table. Its art slot holds
// one or the other and never both.
//
// The two columns are one scroll. A panel with its own overflow scrolls the
// brand away from a form that has not moved, which reads as two pages that
// happen to be side by side. Below the tablet breakpoint the panel stops
// being a column and becomes a band above the form, and on a phone it keeps
// only the wordmark, because a 180px band on a 667px screen is most of the
// space a sign-in form has.
//
// The panel reads top to bottom on one left edge: the wordmark, then the
// copy, then the art or the footer. The wordmark used to be centred by its
// own box while every line under it was set flush left, and the copy sat
// either directly under the mark or halfway down the panel depending on
// whether the screen had art. Now the copy block takes the space between the
// mark and whatever closes the panel and sits in the middle of it, on every
// screen, and runs the full width of the panel's padding.
// ----------------------------------------------------------------------

/**
 * The fleet, photographed. Six files, by URL and never by import.
 *
 * An `import` would put them in the sign-in chunk's module graph, and sign in
 * is the first screen anybody loads and already the slowest thing in the
 * product. A path leaves them as six static files the bundler never sees, so
 * nothing on the way to the form is waiting on 1.2 MB of photography.
 *
 * The directory is `photos/`, not `login/`. nginx serves the web root before
 * it falls through to the SPA, so a directory named after a route shadows
 * the route: `/login` became a 301 to `/login/` and then a 403, and sign in
 * was reachable only by being client-routed there from `/`.
 * `publicRouteShadow.test.ts` holds every `public/` entry off the router's
 * top-level segments.
 */
const FLEET_PHOTOS = [1, 2, 3, 4, 5, 6].map((n) => `/photos/fleet-${n}.jpg`)

/**
 * The panel's secondary ink: every line of copy under the headline.
 *
 * One value for all of it. The standfirst was the panel ink at 72% opacity
 * and the aside under it was the same ink at 64% alpha, in a smaller size,
 * so two paragraphs a few pixels apart were two different greys.
 */
export const PANEL_MUTED = alpha(colorTokens.light.onBrandSurface, 0.72)

/**
 * How long one photograph is held.
 *
 * Deliberately not a motion token and not a candidate to become one: the named
 * motions are the closed set of *movements*, and this is a dwell (how long
 * nothing moves), which the owner picked. The movement between two photographs
 * is `crossfade`, which is where the token belongs.
 *
 * The dwell counts from the start of one arrival to the start of the next, so
 * the still part of a cycle is this minus the crossfade: a little under seven
 * seconds. It is the number to move if the panel ever feels busy, not the
 * fade, which is what makes the exchange read as one photograph becoming
 * another rather than as a cut.
 */
const DWELL_SECONDS = 8

/**
 * The fleet photographs, crossfading in the panel's art slot.
 *
 * Three things about it are load-time decisions rather than styling, and each
 * one is the difference between art and a cost on the slowest screen we have:
 *
 * **The frames only exist after React has mounted.** Nothing here is in
 * `index.html`, preloaded, or in the bundle, so no photograph can be on the
 * path to first paint even in principle: the first request for one is issued
 * after the chunk that draws the form has already run.
 *
 * **A photograph is mounted when the slideshow is about to need it.** The
 * first eagerly, the next one during the current photograph's dwell, and the
 * remaining four not at all until it reaches them. Mounting all six and
 * hiding five with `opacity: 0` would have fetched every one of them at once:
 * they are stacked in the viewport, so `loading="lazy"` does not hold a hidden
 * frame back; the mount is what holds it back.
 *
 * **Below `md` there are no frames at all.** The art slot is `display: none`
 * there, and an eager `<img>` inside a hidden subtree is still fetched, so a
 * phone paid for a photograph it was never going to be shown.
 *
 * Reduced motion is answered here rather than left to the stylesheet. The
 * crossfade is already collapsed by `reducedMotionOverrides`, which drops
 * opacity from the properties a transition may animate, but an eight-second
 * cut is still movement and no media query can reach a `setInterval`. A person
 * who asked for less motion gets the first photograph and nothing after it.
 */
const FleetPhotos = () => {
  const theme = useTheme()
  const hasArtColumn = useResponsive('up', 'md')
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const [current, setCurrent] = useState(0)
  // How many the slideshow has arrived at, which is not the same as which one
  // is showing: the index wraps at the end of a cycle and this does not. Tied
  // to the index, the second lap would have unmounted four loaded frames and
  // mounted them again one at a time, and every photograph after the first
  // would flash back in.
  const [reached, setReached] = useState(1)

  useEffect(() => {
    if (reducedMotion || !hasArtColumn) {
      return undefined
    }
    const dwell = setInterval(() => {
      setCurrent((shown) => (shown + 1) % FLEET_PHOTOS.length)
      setReached((seen) => Math.min(seen + 1, FLEET_PHOTOS.length))
    }, DWELL_SECONDS * 1000)
    // An eight-second interval that outlives the component is a leak that
    // wakes the tab for as long as the session lasts, and sign in unmounts
    // the moment the Tesla round trip comes back.
    return () => clearInterval(dwell)
  }, [reducedMotion, hasArtColumn])

  if (!hasArtColumn) {
    return null
  }

  // One ahead, never six at once. Under reduced motion there is no next
  // photograph, so there is nothing to fetch ahead either.
  const mounted = reducedMotion ? 1 : Math.min(reached + 1, FLEET_PHOTOS.length)

  return (
    <Box
      data-entry-photos
      sx={{
        position: 'relative',
        width: '100%',
        // The six are not one shape (3:2, 4:3 and two panoramas), so the
        // frame decides the shape and the photograph is cropped into it.
        // Letting each one set its own height moved the panel, and the form
        // beside it, every eight seconds.
        aspectRatio: '3 / 2',
        overflow: 'hidden',
        borderRadius: theme.radii.inline,
      }}
    >
      {FLEET_PHOTOS.slice(0, mounted).map((src, index) => (
        <Box
          component="img"
          key={src}
          src={src}
          // Decorative. The photographs say nothing the headline beside them
          // does not, and a screen reader announcing a new one every eight
          // seconds is the panel interrupting the form.
          alt=""
          loading={index === 0 ? 'eager' : 'lazy'}
          decoding="async"
          data-fleet-photo={index}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: index === current ? 1 : 0,
            // The arriving photograph is the one on top, always, including
            // the lap back to the first, where it is otherwise the lowest
            // frame in the stack and would have faded in underneath an opaque
            // photograph and then been revealed by a cut. Not transitioned:
            // the frame it applies to is transparent at the moment it moves.
            zIndex: index === current ? 1 : 0,
            transition:
              index === current
                ? crossfade.transition('opacity')
                : crossfade.hold('opacity'),
          }}
        />
      ))}
    </Box>
  )
}

/**
 * What the console is, under "Run your fleet." on the way in: sign in, the
 * invitation, and setting or resetting a password. One sentence in one place,
 * because four pages spelled it out for themselves and a change to it is a
 * claim about the product, which has to be the same claim on every one.
 *
 * Each item is something a fleet runs end to end in BeastOS. Payments and
 * pricing joined the map, the queue, the rentals and the commands on
 * 2026-10-04.
 */
export const CONSOLE_STANDFIRST =
  'The map, the queue, rentals, pricing, payments and every command, in one console.'

export interface EntryLayoutProps {
  /** The line the panel leads with. Display weight, panel width. */
  headline: string
  /** A sentence under the headline. Optional: the headline can stand alone. */
  standfirst?: string
  /** Which piece of art. The tier's own component, never an icon. */
  art?: IllusProps['name']
  /**
   * The fleet photographs in the art slot instead of an illustration.
   *
   * A separate prop rather than another name on `art`, because it is not the
   * illustration tier and must not be reachable from the tier's own list: the
   * tier is drawn art at a step on a grid, and these are photographs with a
   * download attached. Sign in is the one screen that wants them.
   */
  photos?: boolean
  /**
   * What the panel says under the standfirst: a note, a hint, anything that
   * orients rather than operates. It replaces the art when present, since a
   * panel holding both is a panel doing two jobs.
   *
   * Prose here is the standfirst's role, `body1` in the panel's muted ink,
   * and never a step smaller: the panel used to set three consecutive
   * paragraphs in three sizes. A line that needs to stand out is a hint
   * (`Alert`), which the panel draws in the dark scheme so it reads on the
   * brand surface in both.
   */
  aside?: ReactNode
  /**
   * The quiet row at the foot of the panel: who is signed in, and the way
   * out. Context, not the subject, so it sits away from the task: at the foot
   * of the panel on a wide screen, and at the end of the page on a narrow one.
   *
   * It is drawn in whichever scheme its ground is in, so its ink comes from
   * the theme (`text.secondary`), never from a colour chosen for the panel.
   */
  footer?: ReactNode
  /** The task: a form, a choice, a confirmation. */
  children: ReactNode
}

export default function EntryLayout({
  headline,
  standfirst,
  art,
  photos,
  aside,
  footer,
  children,
}: EntryLayoutProps) {
  // The art slot holds one thing. Photographs win over an illustration, and
  // `aside` replaces either.
  let panelArt: ReactNode = null
  if (photos) {
    panelArt = <FleetPhotos />
  } else if (art) {
    panelArt = <Illus name={art} size="panel" />
  }

  // The brand surface does not invert, so anything the theme draws on it (a
  // hint, a button) is drawn from the scheme that matches the surface rather
  // than from the one the form beside it is in. In the light scheme an info
  // hint here was the pale tint of a light page, a bright slab on the ink.
  const panelTheme = useMemo(() => createAppTheme('dark'), [])

  // Where the foot goes. Shown at every width, because it holds a control: the
  // way out. A person with no provider is redirected here with `replace`, so
  // Back is gone, and hiding the sign out left them on a screen with no
  // forward action and no way off it.
  //
  // Beside the brand panel on a wide screen, and at the end of the page below
  // `md`, where the panel is a band above the form. In the band it was the
  // first thing on a phone: who is signed in and how to leave, above the form
  // the screen exists for. One node in one place rather than two copies
  // hidden in turn, so there is never a second Sign out for a screen reader
  // or a spec to find, and the foot takes whichever scheme its ground is in.
  // `noSsr` reads the query on the first render, so a desktop never draws the
  // foot at the bottom for a frame.
  const outerTheme = useTheme()
  const footInPanel = useMediaQuery(outerTheme.breakpoints.up('md'), {
    noSsr: true,
  })

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      sx={{ minHeight: '100vh', bgcolor: 'background.default' }}
    >
      <Stack
        sx={{
          'position': 'relative',
          'overflow': 'hidden',
          'flexShrink': 0,
          'width': { xs: '100%', md: layout.entryPanelWidth },
          'minHeight': { xs: layout.entryPanelBandHeight, md: 'auto' },
          // Fixed, not from the neutral ramp. `grey[900]` is `ink`, which
          // inverts with the scheme: in dark mode the panel came out white
          // and the console opened with the brightest thing on the screen
          // being a slab next to a dark form. Contrast still passed, which is
          // why nothing caught it. A brand surface is closer to a photograph
          // than to a control, so it holds still.
          'bgcolor': colorTokens.light.brandSurface,
          'color': colorTokens.light.onBrandSurface,
          'p': { xs: 3, md: 5 },
          'gap': 3,
          'justifyContent': { xs: 'center', md: 'space-between' },
          // The accent bleeds in from the corner rather than sitting as a
          // shape. It is the one bold colour on the screen.
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: layout.entryPanelWidth,
            height: layout.entryPanelWidth,
            transform: 'translate(30%, -30%)',
            borderRadius: '50%',
            background: (theme) =>
              `radial-gradient(circle, ${alpha(
                theme.palette.primary.main,
                0.35,
              )}, transparent 70%)`,
            pointerEvents: 'none',
          },
        }}
      >
        <ThemeProvider theme={panelTheme}>
          {/* The lead step: here the wordmark is the page's own mark with no
              tile beside it to be measured against, so it is sized against
              the copy under it. 24 and 28 were the same role written twice.

              Flush left with the copy. A block-level svg in a column is
              stretched to the column's width and draws its mark in the middle
              of it, which is how the wordmark came to be centred over a
              headline set flush left. */}
          <BeastWordmark
            sx={{
              height: layout.brandWordmarkLead,
              position: 'relative',
              flexShrink: 0,
              alignSelf: 'flex-start',
            }}
          />

          {/* The headline and the art are what the band drops on a phone. The
              wordmark stays, so the screen is still identifiably Beast.

              The block grows into the space between the wordmark and what
              closes the panel, and centres itself in it, so the copy never
              sits hard under the mark. */}
          <Stack
            data-entry-copy
            sx={{
              display: { xs: 'none', sm: 'flex' },
              position: 'relative',
              flexGrow: 1,
              justifyContent: 'center',
              gap: 3,
            }}
          >
            <Stack sx={{ gap: 2 }}>
              <Typography variant="h1" sx={{ color: 'inherit' }}>
                {headline}
              </Typography>
              {/* No measure of its own. It was held to 36ch, so it broke
                  after half the panel while the aside under it ran the full
                  width, and the two read as different columns. `pretty`
                  keeps the last word of a sentence off a line of its own now
                  that the line is the panel's width. */}
              {standfirst && (
                <Typography
                  variant="body1"
                  sx={{ color: PANEL_MUTED, textWrap: 'pretty' }}
                >
                  {standfirst}
                </Typography>
              )}
            </Stack>

            {aside && (
              <Stack
                sx={{
                  gap: 3,
                  typography: 'body1',
                  color: PANEL_MUTED,
                }}
              >
                {aside}
              </Stack>
            )}
          </Stack>

          {panelArt && !aside && (
            <Box
              data-entry-art
              sx={{
                display: { xs: 'none', md: 'flex' },
                position: 'relative',
                alignItems: 'center',
                justifyContent: 'center',
                // An illustration sits in a mat and a photograph does not.
                //
                // The illustration is a few strokes on the panel's own ground,
                // so it needs an edge to be an object at all. A photograph
                // already is one: it brings its own contrast, which we do not
                // control, and a hairline laid over it has no floor to clear:
                // white on a white bonnet in one of these six and invisible.
                // Matting it instead turns the panel's only image into a
                // thumbnail floating in a large empty box, which is what this
                // shipped as.
                //
                // So the photograph fills the frame and the frame is its own
                // corner radius; the illustration keeps the mat and the border
                // on the panel surface, with brand ink either side of it.
                ...(photos
                  ? { borderRadius: (theme) => theme.radii.surface }
                  : {
                      p: 3,
                      border: '1px solid',
                      borderColor: (theme) =>
                        alpha(theme.palette.common.white, 0.16),
                      borderRadius: (theme) => theme.radii.surface,
                    }),
                overflow: 'hidden',
              }}
            >
              {panelArt}
            </Box>
          )}

          {footer && footInPanel && (
            <Box
              data-entry-foot="panel"
              sx={{
                position: 'relative',
                pt: 3,
                borderTop: '1px solid',
                borderColor: (theme) => alpha(theme.palette.common.white, 0.16),
              }}
            >
              {footer}
            </Box>
          )}
        </ThemeProvider>
      </Stack>

      <Stack
        sx={{
          flexGrow: 1,
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 3, md: 5 },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: layout.entryFormWidth }}>
          {children}

          {footer && !footInPanel && (
            <Box
              data-entry-foot="page"
              sx={{
                mt: 4,
                pt: 3,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              {footer}
            </Box>
          )}
        </Box>
      </Stack>
    </Stack>
  )
}
